// Casting a PoKu stream to a TV — Chromecast via the Remote Playback API,
// AirPlay via WebKit's picker. No Google Cast SDK, no registered receiver, no
// per-deployment configuration: both routes ship with the browser.
//
//   remote   `video.remote.prompt()` — Chrome/Edge desktop and Android Chrome.
//            The browser's own device picker lists the Cast devices on the
//            network, and the device then fetches the media URL itself.
//   airplay  `video.webkitShowPlaybackTargetPicker()` — Safari (desktop and
//            iOS) and the packaged app's WebView, which additionally needs the
//            `x-webkit-airplay="allow"` attribute on the element (TorrentVideo
//            sets it).
//
// The receiver loads the URL itself, so what CANNOT be cast is anything the page
// is producing locally:
//   • a MediaSource (hls.js remuxes every HLS package on Chrome/Android) has no
//     URL to hand over — `blob:` in currentSrc is that case,
//   • `data:`/`filesystem:` URLs are page-local,
//   • a loopback URL (the torrent stream server on 127.0.0.1) is unreachable
//     from a TV.
// For those the button is not rendered at all rather than failing after a tap.
// Casting HLS *from Chrome* needs the Cast sender framework with the Default
// Media Receiver, which is deliberately not wired here.
//
// Every function takes a video-like object (not a ref), so the rules are
// unit-testable in node — see scripts/test-cast.mjs.

const LOOPBACK = /^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?::\d+)?(?:\/|$)/i;

/** "remote" | "airplay" | null — which native picker this element can open. */
export function castRoute(video) {
  if (!video) return null;
  if (video.remote && typeof video.remote.prompt === "function") return "remote";
  if (typeof video.webkitShowPlaybackTargetPicker === "function") return "airplay";
  return null;
}

/** True when a URL is one a TV can fetch for itself. */
export function canCastSrc(src) {
  const value = String(src || "").trim();
  if (!value) return false;
  if (/^(blob|data|filesystem):/i.test(value)) return false;
  if (LOOPBACK.test(value)) return false;
  // An absolute URL, or a same-origin path the receiver resolves against us.
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("/");
}

/** The URL the element is actually playing, once one is loaded. */
export function currentSrcOf(video) {
  if (!video) return "";
  return String(video.currentSrc || video.src || "");
}

export function canCastElement(video) {
  if (!video || !castRoute(video)) return false;
  return canCastSrc(currentSrcOf(video));
}

export function isCastConnected(video) {
  if (!video) return false;
  if (castRoute(video) === "remote") return video.remote.state === "connected";
  return video.webkitCurrentPlaybackTargetIsWireless === true;
}

/**
 * Everything the cast button needs to render. `availability` comes from the
 * browser's own watch (null = not known yet, which stays optimistic: the button
 * is shown and `prompt()` reports the truth after a tap).
 */
export function describeCast(video, availability = null) {
  return {
    route: castRoute(video),
    castable: canCastElement(video),
    connected: isCastConnected(video),
    available: availability !== false,
  };
}

/**
 * Opens the device picker. Resolves with:
 *   "remote" | "airplay"  the picker opened (playback may still be cancelled)
 *   "unavailable"         no device answered (NotAllowedError)
 *   "unsupported"         this element cannot hand its media over (MSE, no src)
 *   "cancelled"           the user dismissed the picker
 */
export function startCast(video) {
  const route = castRoute(video);
  if (!route) return Promise.resolve("unsupported");
  if (!canCastElement(video)) return Promise.resolve("unsupported");
  if (route === "airplay") {
    video.webkitShowPlaybackTargetPicker();
    return Promise.resolve("airplay");
  }
  return video.remote.prompt().then(
    () => "remote",
    (error) => {
      const name = error?.name || "";
      if (name === "NotAllowedError" || name === "NotFoundError") return "unavailable";
      if (name === "NotSupportedError" || name === "InvalidStateError") return "unsupported";
      if (name === "AbortError") return "cancelled";
      return "cancelled";
    },
  );
}

/**
 * Calls `onChange(describeCast(video, availability))` on mount and whenever the
 * answer can change: the element loading/emptying a source, the remote
 * connection changing state, and the browser's own device-availability watch.
 * Returns an unsubscribe function.
 */
export function watchCast(video, onChange) {
  if (!video || typeof onChange !== "function") return () => {};
  let disposed = false;
  let availability = null;
  const emit = () => {
    if (!disposed) onChange(describeCast(video, availability));
  };
  const onAvailability = (event) => {
    availability = event?.availability === "available";
    emit();
  };

  const elementEvents = ["loadedmetadata", "canplay", "emptied", "webkitcurrentplaybacktargetiswirelesschanged"];
  elementEvents.forEach((name) => video.addEventListener(name, emit));
  video.addEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);

  const remote = castRoute(video) === "remote" ? video.remote : null;
  const remoteEvents = ["connect", "connecting", "disconnect"];
  if (remote) {
    remoteEvents.forEach((name) => remote.addEventListener(name, emit));
    try {
      const watching = remote.watchAvailability((available) => {
        availability = available === true;
        emit();
      });
      // Per the spec's own guidance: if continuous watching is refused, leave
      // availability unknown (the button stays visible and prompt() reports the
      // truth after a tap).
      if (watching && typeof watching.catch === "function") {
        watching.catch(() => {
          availability = null;
          emit();
        });
      }
    } catch {
      availability = null;
    }
  }

  emit();

  return () => {
    disposed = true;
    elementEvents.forEach((name) => video.removeEventListener(name, emit));
    video.removeEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);
    if (remote) {
      remoteEvents.forEach((name) => remote.removeEventListener(name, emit));
      try {
        remote.cancelWatchAvailability?.();
      } catch {
        /* nothing to cancel */
      }
    }
  };
}
